/** Wire types. These mirror the Pydantic models in server/app/models.py. */

/**
 * The commit this SPA bundle was built from, injected by deploy.yml.
 *
 * Shown in the footer. The SPA and the API deploy separately, so either can be
 * stale, and a cached bundle is indistinguishable from a fix that did not work
 * until you can read the build back. "dev" locally.
 */
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev'

export type SearchBy = 'address' | 'property'

/**
 * Fallback State list, used only when the lookup cannot be reached.
 *
 * The real list comes from `spGetLocalityStates` via GET /api/peril/suggest,
 * because a hardcoded one was wrong: it had eight states and territories and
 * production offers nine, the missing one being OT for Other Territories
 * (Jervis Bay, Norfolk Island, Christmas Island and the Cocos Islands).
 *
 * Kept as a fallback rather than removed, so a lookup that fails leaves a usable
 * form instead of an empty dropdown. Ordered alphabetically after ALL, matching
 * production: QA read the old order, by population, as values being missing.
 */
export const STATE_OPTIONS = [
  'ALL',
  'ACT',
  'NSW',
  'NT',
  'OT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
] as const

/** Flood Score domain: 1 is least risk, 6 is riskiest. Blank where unrecorded. */
export const FLOOD_SCORE_MIN = 1
export const FLOOD_SCORE_MAX = 6

/**
 * Bushfire Attack Level is an integer category code in the Allianz peril
 * dataset, not an AS 3959 BAL-12.5 style rating. -1 means Unknown and is a real
 * recorded value, distinct from no data at all.
 */
export const BUSHFIRE_CODE_MIN = -1
export const BUSHFIRE_CODE_MAX = 5

export interface PerilRecord {
  recordRef: string
  gnafPid: string | null
  perilAddress: string | null
  propertyStreetAddress: string | null
  floodScore: number | null
  floodAriGl: number | null
  floodAriGl1m: number | null
  floodAriGl2m: number | null
  elevation: number | null
  arpcWindZone: string | null
  arpcWindBand: string | null
  arpcFloodBand: string | null
  arpcSurgeBand: string | null
  bushfireAttackLevel: number | null
  bushfireAttackLevelCategory: string | null
  /** "<priority>. <name>", for example "3. NFIDH". */
  source: string | null
  primaryRef: boolean | null
}

export interface PerilSearchResponse {
  items: PerilRecord[]
  page: number
  pageSize: number
  hasMore: boolean
  totalItems: number | null
  totalPages: number | null
}

/** Group order matches the legacy K2 details view. */
export interface PerilRecordDetail {
  recordRef: string
  perilAddress: string | null
  property: {
    gnafPid: string | null
    source: string | null
    isPrimaryReference: boolean | null
  }
  address: {
    unitNumber: string | null
    streetNumberFrom: string | null
    streetNumberTo: string | null
    streetName: string | null
    streetType: string | null
    suburb: string | null
    state: string | null
    postcode: string | null
    propertyKey: string | null
    strataPlanNumber: string | null
  }
  flood: {
    floodScore: number | null
    floodAriGl: number | null
    floodAriGl1m: number | null
    floodAriGl2m: number | null
    elevation: number | null
  }
  bushfire: {
    attackLevel: number | null
    attackLevelCategory: string | null
  }
  arpc: {
    windZone: string | null
    windBand: string | null
    floodBand: string | null
    surgeBand: string | null
  }
}

/**
 * Address fields the search form suggests values for as the underwriter types,
 * as the legacy K2 form does. Street number and unit number are plain inputs
 * there, and neither has a useful value list.
 */
export type SuggestField = 'postcode' | 'suburb' | 'streetName' | 'state'

export interface PerilSuggestResponse {
  field: SuggestField
  term: string
  items: string[]
  /** True when more matched than the limit allowed, so the list is a head. */
  hasMore: boolean
}

/** Mirrors the API's Principal. No roles: access is an Entra concern. */
export interface CurrentUser {
  /** Entra object ID. */
  oid: string
  name: string
  preferredUsername: string | null
}

/** The search form's state. */
export interface SearchCriteria {
  searchBy: SearchBy
  state: string
  postcode: string
  suburb: string
  streetName: string
  streetNumber: string
  unitNumber: string
  propertyKey: string
  strataPlanNumber: string
  mostReliableOnly: boolean
  includeUnits: boolean
  includeBushfire: boolean
}

/** Defaults match the legacy K2 form's initial state. */
export const EMPTY_CRITERIA: SearchCriteria = {
  searchBy: 'address',
  state: 'ALL',
  postcode: '',
  suburb: '',
  streetName: '',
  streetNumber: '',
  unitNumber: '',
  propertyKey: '',
  strataPlanNumber: '',
  mostReliableOnly: true,
  includeUnits: false,
  includeBushfire: true,
}

/**
 * Grid sort. `sortBy` is a PerilRecord field name, or null for the default
 * order, which is the legacy grid's: street name, then street type, then street
 * number.
 *
 * The server sorts, not the SPA. The procedures return the whole match set and
 * this application pages it, so sorting in the browser would sort the thirty
 * rows on screen and present them as the extremes of a set of thousands.
 */
export interface SortState {
  sortBy: keyof PerilRecord | null
  sortDir: 'asc' | 'desc'
}

export const DEFAULT_SORT: SortState = { sortBy: null, sortDir: 'asc' }

/**
 * Columns the grid can sort by. Mirrors SORTABLE_COLUMNS in
 * server/app/data/procedures.py.
 *
 * `primaryRef` is deliberately absent: the legacy K2 grid offers no sort on it,
 * confirmed by QA against production on 30/08/2026.
 */
export const SORTABLE_COLUMNS: ReadonlySet<keyof PerilRecord> = new Set([
  'gnafPid',
  'perilAddress',
  'propertyStreetAddress',
  'floodScore',
  'floodAriGl',
  'floodAriGl1m',
  'floodAriGl2m',
  'elevation',
  'arpcWindZone',
  'arpcWindBand',
  'arpcFloodBand',
  'arpcSurgeBand',
  'bushfireAttackLevel',
  'bushfireAttackLevelCategory',
  'source',
])
